/**
 * 포트폴리오 편집 추적 훅
 * 
 * 포트폴리오 종목 편집의 의도-결과를 추적
 * - port_edit_start: 편집 시작
 * - port_edit_success: 편집 성공
 * - port_edit_fail: 편집 실패
 * 
 * @example
 * ```tsx
 * function PortfolioEditPage() {
 *   const portEdit = usePortEdit();
 *   
 *   const handleEdit = async (portfolioId: number, prevItems: PortfolioItem[], newItems: PortfolioItem[]) => {
 *     portEdit.start({
 *       portfolio_id: portfolioId,
 *       prev_items: prevItems,
 *     });
 *     
 *     try {
 *       await updatePortfolioAPI(portfolioId, newItems);
 *       portEdit.success({
 *         portfolio_id: portfolioId,
 *         cur_items: newItems,
 *       });
 *     } catch (error) {
 *       portEdit.fail('backend_5xx');
 *     }
 *   };
 * }
 * ```
 */

import { useCallback, useRef } from 'react';
import { useIntent } from '../useIntent';
import { track } from '../../../../core/client';
import type { PortfolioItem } from '../../../../core/schema';

export interface PortEditStartContext {
    portfolio_id: number;
    prev_items: PortfolioItem[];
}

export interface PortEditSuccessContext {
    portfolio_id: number;
    cur_items: PortfolioItem[];
}

export interface PortEditActions {
    start: (context: PortEditStartContext, options?: {
        parentIntentId?: string;
        parentIntentType?: string;
    }) => string;
    success: (context: PortEditSuccessContext) => void;
    fail: (reason?: string) => void;
}

/**
 * 포트폴리오 편집 추적 훅
 */
export function usePortEdit(): PortEditActions {
    // success 시 사용할 cur_items를 ref로 저장
    const curItemsRef = useRef<PortfolioItem[]>([]);

    const intent = useIntent<PortEditStartContext>({
        type: 'port_edit',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            track('port_edit_start', {
                intent_id,
                portfolio_id: context!.portfolio_id,
                prev_items: context!.prev_items,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            track('port_edit_success', {
                intent_id,
                portfolio_id: context!.portfolio_id,
                cur_items: curItemsRef.current,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            track('port_edit_fail', {
                intent_id,
                portfolio_id: context!.portfolio_id,
                fail_reason,
            });
        },
    });

    const success = useCallback((successContext: PortEditSuccessContext) => {
        // success 호출 시 cur_items를 ref에 저장
        curItemsRef.current = successContext.cur_items;
        intent.success();
    }, [intent]);

    return {
        start: (context, options) => intent.start(context, options),
        success,
        fail: (reason) => intent.fail(reason),
    };
}

export type { PortfolioItem };

